import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useTranslation } from "../i18n";
import PhotoGallery from "./PhotoGallery";
import { listEvents } from "../lib/galleryService";
import "../styles/EventGallery.css";

const EventGallery = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lightboxFlyer, setLightboxFlyer] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await listEvents();
        setEvents(data);
      } catch (err) {
        console.error("[EventGallery] fetch error:", err);
        setError(t("gallery.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();

  }, [t]);


  const navigate = useNavigate();

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (lightboxFlyer) {
          setLightboxFlyer(null);
        } else if (selectedEvent) {
          setSelectedEvent(null);
        } else {
          navigate('/');
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedEvent, lightboxFlyer, navigate]);


  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleEventClick = (event) => {
    if (event.photoCount > 0) {
      setSelectedEvent(event);
    } else {

      setLightboxFlyer(event.flyer.url);
    }
  };


  if (selectedEvent) {
    return (
      <div className="event-gallery-overlay">
        <PhotoGallery
          eventId={selectedEvent.id}
          eventName={selectedEvent.displayName}
          onBack={() => setSelectedEvent(null)}
        />
      </div>
    );
  }

  return (
    <>
      <div className="event-gallery-overlay">
        <div className="event-gallery">
          <div className="event-gallery-header">
            <h2 className="event-gallery-title">{t("gallery.title")}</h2>
            <button
              className="event-gallery-back"
              onClick={() => navigate('/')}
            >
              ✕
            </button>
          </div>

          {loading && (
            <p className="event-gallery-status">{t("gallery.loading")}</p>
          )}
          {error && (
            <p className="event-gallery-status event-gallery-error">{error}</p>
          )}

          {!loading && !error && events.length === 0 && (
            <p className="event-gallery-status">{t("gallery.empty")}</p>
          )}

          <div className="event-grid">
            {events.map((event) => (
              <div
                key={event.id}
                className="event-card"
                onClick={() => handleEventClick(event)}
              >
                <div className="event-card-img-wrapper">
                  <img
                    src={event.flyer.url}
                    alt={event.displayName}
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
                <div className="event-card-info">
                  <h3>{event.displayName}</h3>
                  <span className="event-card-count">
                    {event.photoCount > 0
                      ? t("gallery.photoCount", { count: event.photoCount })
                      : t("gallery.flyer")}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {}
      {lightboxFlyer &&
        createPortal(
          <div
            className="gallery-lightbox"
            onClick={() => setLightboxFlyer(null)}
          >
            <div
              className="gallery-lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="gallery-lightbox-close"
                onClick={() => setLightboxFlyer(null)}
              >
                ✕
              </button>
              <img
                src={lightboxFlyer}
                alt={t("gallery.flyer")}
                className="gallery-lightbox-image"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default EventGallery;
